import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../../components/layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Table from '../../components/ui/Table';
import Modal from '../../components/ui/Modal';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Badge from '../../components/ui/Badge';
import Loading from '../../components/ui/Loading';
import Alert from '../../components/ui/Alert';
import EdgeSyncModal from '../../components/ui/EdgeSyncModal';
import EdgeProvisionModal from '../../components/ui/EdgeProvisionModal';
import { Plus, Camera as CameraIcon, Edit2, Trash2, Video, Search, Eye, Download, Settings, ChevronLeft, ChevronRight } from 'lucide-react';
import { cameraService, locationService } from '../../services';
import { useLocation as useLocationContext } from '../../contexts/LocationContext';
import { usePermission } from '../../hooks';
import { SCREEN_CODES } from '../../constants/screenCodes';
import type { Camera } from '../../types';

// Helper function to check if camera is online based on protocol_status
const isCameraOnline = (camera: Camera): boolean => {
  // Check protocol_status for any connected protocol
  if (camera.protocol_status && typeof camera.protocol_status === 'object') {
    const statuses = Object.values(camera.protocol_status) as any[];
    // Camera is online if any protocol shows 'connected' status
    const isOnline = statuses.some(status => {
      if (typeof status === 'string') {
        return status === 'connected' || status === 'online';
      }
      if (typeof status === 'object' && status !== null) {
        return status?.status === 'connected' || 
               status?.status === 'online' ||
               status?.connection_status === 'connected' ||
               status?.connected === true;
      }
      return false;
    });
    return isOnline;
  }
  // Fallback to status field if protocol_status is not available
  return camera.status === true;
};

const CamerasPage: React.FC = () => {
  const navigate = useNavigate();
  const { selectedLocationId: selectedLocation, locations } = useLocationContext();
  const { canView, canCreate, canEdit, canDelete, canExport } = usePermission(SCREEN_CODES.CAMERAS);
  const [cameras, setCameras] = useState<Camera[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEdgeSyncModalOpen, setIsEdgeSyncModalOpen] = useState(false);
  const [isEdgeProvisionModalOpen, setIsEdgeProvisionModalOpen] = useState(false);
  const [selectedCamera, setSelectedCamera] = useState<Camera | null>(null);
  const [isCloudSyncing, setIsCloudSyncing] = useState(false);
  const [cloudSyncSuccess, setCloudSyncSuccess] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    camera_location: '',
    camera_company: '',
    camera_mac: '',
    camera_model: '',
    camera_ip: '',
    camera_port: '',
    camera_username: '',
    camera_password: '',
    location_id: '',
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Status counts (from ALL cameras, not just current page)
  const [onlineCount, setOnlineCount] = useState(0);
  const [offlineCount, setOfflineCount] = useState(0);

  const cameraRequestRef = useRef(0);
  const cameraLocationRef = useRef(selectedLocation);
  cameraLocationRef.current = selectedLocation;
  const fetchCameras = useCallback(async () => {
    if (selectedLocation !== cameraLocationRef.current) return;
    const request = ++cameraRequestRef.current;
    try {
      setIsLoading(true);
      setError(null);
      const filters = [];
      if (selectedLocation !== 'all') filters.push(`location_id::${selectedLocation}`);
      if (searchTerm.trim()) filters.push(`camera_location::like::${searchTerm.trim()}`);
      const response = await cameraService.getCameras({ page: currentPage, size: itemsPerPage, filter: filters.join(',') || undefined });
      if (request !== cameraRequestRef.current) return;
      const data = response.data || [];
      setCameras(data);
      setTotalElements(response.totalElements ?? data.length);
      setTotalPages(response.totalPages ?? Math.ceil((response.totalElements ?? data.length) / itemsPerPage));
      const online = data.filter(isCameraOnline).length;
      setOnlineCount(response.onlineCount ?? online);
      setOfflineCount(response.offlineCount ?? data.length - online);
    } catch (err) {
      if (request === cameraRequestRef.current) setError(err instanceof Error ? err.message : 'Failed to fetch cameras');
    } finally {
      if (request === cameraRequestRef.current) setIsLoading(false);
    }
  }, [selectedLocation, searchTerm, currentPage, itemsPerPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedLocation]);

  useEffect(() => {
    void fetchCameras();
    return () => { cameraRequestRef.current += 1; };
  }, [fetchCameras]);

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    if (!formData.camera_location.trim()) errors.camera_location = 'Camera location is required';
    if (!formData.camera_company.trim()) errors.camera_company = 'Company is required';
    if (!formData.camera_mac.trim()) errors.camera_mac = 'MAC address is required';
    if (!formData.camera_model.trim()) errors.camera_model = 'Model is required';
    if (!formData.camera_ip.trim()) errors.camera_ip = 'IP address is required';
    if (!formData.camera_port.trim()) errors.camera_port = 'Port is required';
    if (!formData.camera_username.trim()) errors.camera_username = 'Username is required';
    if (!formData.camera_password.trim()) errors.camera_password = 'Password is required';
    if (!formData.location_id) errors.location_id = 'Location is required';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const resetForm = () => {
    setFormData({
      camera_location: '',
      camera_company: '',
      camera_mac: '',
      camera_model: '',
      camera_ip: '',
      camera_port: '',
      camera_username: '',
      camera_password: '',
      location_id: '',
    });
    setFormErrors({});
  };

  const handleCreate = async () => {
    if (!validateForm()) return;

    try {
      await cameraService.createCamera({
        ...formData,
        camera_port: parseInt(formData.camera_port),
        location_id: parseInt(formData.location_id),
      });
      setIsCreateModalOpen(false);
      resetForm();
      fetchCameras();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create camera');
    }
  };

  const handleDelete = async () => {
    if (!selectedCamera) return;

    try {
      await cameraService.deleteCamera(selectedCamera.camera_id);
      setIsDeleteModalOpen(false);
      setSelectedCamera(null);
      fetchCameras();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete camera');
    }
  };

  const openDeleteModal = (camera: Camera) => {
    setSelectedCamera(camera);
    setIsDeleteModalOpen(true);
  };

  const columns = [
    {
      key: 'camera_location',
      label: 'Camera',
      render: (camera: Camera) => (
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
            isCameraOnline(camera) ? 'bg-status-success/10' : 'bg-surface-secondary'
          }`}>
            <CameraIcon size={18} className={isCameraOnline(camera) ? 'text-status-success' : 'text-text-muted'} />
          </div>
          <div>
            <div className="font-medium text-text-primary">{camera.name || camera.camera_location}</div>
            <div className="text-xs text-text-muted">
              {camera.brand || 'Unknown'} • {camera.model || camera.camera_model || 'N/A'}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (camera: Camera) => (
        <Badge variant={isCameraOnline(camera) ? 'success' : 'danger'} dot size="sm">
          {isCameraOnline(camera) ? 'Online' : 'Offline'}
        </Badge>
      ),
    },
    {
      key: 'camera_purpose',
      label: 'Purpose',
      render: (camera: Camera) => {
        const purposeLabels: Record<string, string> = {
          'general': 'General',
          'id_capture': 'ID Capture',
          'face_recognition': 'Face Recognition',
          'license_plate': 'License Plate',
          'vehicle_front': 'Vehicle Front',
          'vehicle_right': 'Vehicle Right',
          'vehicle_left': 'Vehicle Left',
          'vehicle_back': 'Vehicle Back',
        };
        const label = camera.camera_purpose ? purposeLabels[camera.camera_purpose] || camera.camera_purpose : '-';
        return <div className="text-sm text-text-primary">{label}</div>;
      },
    },
    {
      key: 'enabled_features',
      label: 'Features',
      render: (camera: Camera) => {
        const featureLabels: Record<string, string> = {
          'motion_detection': 'Motion',
          'line_crossing': 'Line Cross',
          'intrusion_detection': 'Intrusion',
          'face_detection': 'Face',
          'vehicle_detection': 'Vehicle',
          'license_plate': 'LPR',
          'audio': 'Audio',
          'ptz': 'PTZ',
        };
        
        if (!camera.enabled_features || typeof camera.enabled_features !== 'object') {
          return <div className="text-sm text-text-muted">-</div>;
        }
        
        const enabledList = Object.entries(camera.enabled_features)
          .filter(([_, enabled]) => enabled === true)
          .map(([feature]) => featureLabels[feature] || feature);
        
        if (enabledList.length === 0) {
          return <div className="text-sm text-text-muted">None</div>;
        }
        
        return (
          <div className="flex flex-wrap gap-1">
            {enabledList.map((feature) => (
              <Badge key={feature} variant="info" size="sm">
                {feature}
              </Badge>
            ))}
          </div>
        );
      },
    },
    {
      key: 'last_seen',
      label: 'Last Seen',
      render: (camera: Camera) => {
        try {
          if (!camera.last_seen) return <div className="text-sm text-text-muted">Never</div>;
          const date = new Date(camera.last_seen);
          if (isNaN(date.getTime())) return <div className="text-sm text-text-muted">-</div>;
          return (
            <div className="text-sm text-text-muted">
              {date.toLocaleDateString()} {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </div>
          );
        } catch {
          return <div className="text-sm text-text-muted">-</div>;
        }
      },
    },
    {
      key: 'network',
      label: 'Network',
      render: (camera: Camera) => (
        <div>
          <div className="text-text-primary text-sm">
            {camera.ip_address || camera.camera_ip}:{camera.http_port || camera.camera_port}
          </div>
          <div className="text-xs text-text-muted">MAC: {camera.camera_mac || 'N/A'}</div>
        </div>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      align: 'right' as const,
      render: (camera: Camera) => (
        <div className="flex items-center justify-end gap-2">
          {canView && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/cameras/${camera.camera_id}/view`);
              }}
              leftIcon={<Eye size={14} />}
            >
              View
            </Button>
          )}
          {canEdit && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/cameras/${camera.camera_id}/edit`);
              }}
              leftIcon={<Edit2 size={14} />}
            >
              Edit
            </Button>
          )}
          {canDelete && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                openDeleteModal(camera);
              }}
              leftIcon={<Trash2 size={14} />}
              className="text-status-danger hover:text-status-danger"
            >
              Delete
            </Button>
          )}
        </div>
      ),
    },
  ];

  const companyOptions = [
    { label: 'Select Company', value: '' },
    { label: 'Hikvision', value: 'Hikvision' },
    { label: 'Dahua', value: 'Dahua' },
    { label: 'Axis', value: 'Axis' },
    { label: 'Hanwha', value: 'Hanwha' },
    { label: 'Other', value: 'Other' },
  ];

  // Use counts from API (all cameras) instead of calculating from current page
  const onlineCameras = onlineCount;
  const offlineCameras = offlineCount;

  // Pagination display calculation (data is already paginated from server)
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + cameras.length, totalElements);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (value: string) => {
    setItemsPerPage(parseInt(value));
    setCurrentPage(1);
  };

  const handleProvisionClick = async () => {
    if (selectedLocation === 'all') return;

    // Get the selected location details
    const location = locations.find(loc => String(loc.ycl_id) === selectedLocation);
    
    if (!location) {
      setError('Location not found');
      return;
    }

    // Check if this is a cloud location (has api_key and app_secret)
    const isCloudLocation = location.api_key && location.app_secret;

    if (isCloudLocation) {
      // Cloud location - sync cameras from cloud provider
      setIsCloudSyncing(true);
      setError(null);
      setCloudSyncSuccess(null);
      
      try {
        const response = await locationService.syncCloudCameras(location.ycl_id);
        const { sync_result } = response;
        
        if (sync_result.success) {
          const { cameras_synced, cameras_failed } = sync_result;
          setCloudSyncSuccess(
            `✅ Cloud sync completed! Synced ${cameras_synced} camera${cameras_synced !== 1 ? 's' : ''}` +
            (cameras_failed > 0 ? `. ${cameras_failed} camera${cameras_failed !== 1 ? 's' : ''} failed.` : '')
          );
          // Refresh camera list
          await fetchCameras();
        } else {
          setError(sync_result.message || 'Cloud sync failed');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to sync cameras from cloud');
      } finally {
        setIsCloudSyncing(false);
      }
    } else {
      // Custom NVR location - open edge provision modal
      setIsEdgeProvisionModalOpen(true);
    }
  };

  return (
    <MainLayout>
      <div className="page-stack">
        {/* Header */}
        <div className="page-header">
          <div>
            <h1 className="page-title">Cameras</h1>
            <p className="page-description">Monitor and manage your security cameras</p>
          </div>
          <div className="page-actions">
            <Button
              variant="outline"
              size="md"
              leftIcon={<Settings size={18} />}
              onClick={handleProvisionClick}
              disabled={selectedLocation === 'all' || isCloudSyncing}
              title={
                selectedLocation === 'all' 
                  ? 'Select a location first' 
                  : isCloudSyncing
                  ? 'Syncing cameras from cloud...'
                  : 'Provision/Sync cameras'
              }
            >
              {isCloudSyncing ? 'Syncing...' : 'Provision'}
            </Button>
            <Button
              variant="outline"
              size="md"
              leftIcon={<Download size={18} />}
              onClick={() => setIsEdgeSyncModalOpen(true)}
              disabled={selectedLocation === 'all'}
              title={selectedLocation === 'all' ? 'Select a location first' : 'Sync cameras from edge device'}
            >
              Sync from Edge
            </Button>
            {canCreate && (
              <Button
                variant="primary"
                size="md"
                leftIcon={<Plus size={18} />}
                onClick={() => {
                  resetForm();
                  setIsCreateModalOpen(true);
                }}
              >
                Add Camera
              </Button>
            )}
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert
            variant="error"
            message={error}
            onClose={() => setError(null)}
          />
        )}

        {/* Success Alert */}
        {cloudSyncSuccess && (
          <Alert
            variant="success"
            message={cloudSyncSuccess}
            onClose={() => setCloudSyncSuccess(null)}
          />
        )}

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 lg:gap-4">
          <Card padding="md">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-text-muted text-xs sm:text-sm mb-1">Total Cameras</div>
                <div className="text-2xl font-bold text-text-primary">{totalElements}</div>
              </div>
              <div className="hidden sm:flex w-10 h-10 bg-accent-primary/10 rounded-lg items-center justify-center">
                <CameraIcon size={20} className="text-accent-primary" />
              </div>
            </div>
          </Card>
          <Card padding="md">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-text-muted text-xs sm:text-sm mb-1">Online</div>
                <div className="text-2xl font-bold text-status-success">{onlineCameras}</div>
              </div>
              <div className="hidden sm:flex w-10 h-10 bg-status-success/10 rounded-lg items-center justify-center">
                <Video size={20} className="text-status-success" />
              </div>
            </div>
          </Card>
          <Card padding="md">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-text-muted text-xs sm:text-sm mb-1">Offline</div>
                <div className="text-2xl font-bold text-status-danger">{offlineCameras}</div>
              </div>
              <div className="hidden sm:flex w-10 h-10 bg-status-danger/10 rounded-lg items-center justify-center">
                <CameraIcon size={20} className="text-status-danger" />
              </div>
            </div>
          </Card>
        </div>

        {/* Search and Filters */}
        <Card padding="md">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
            <div className="flex-1">
              <Input
                placeholder="Search cameras by name, IP, MAC..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                leftIcon={<Search size={18} />}
                fullWidth
              />
            </div>

          </div>
        </Card>

        {/* Cameras Table */}
        <Card padding="none">
          {isLoading ? (
            <div className="p-12">
              <Loading size="lg" text="Loading cameras..." />
            </div>
          ) : (
            <>
              <Table
                data={cameras}
                columns={columns}
                isLoading={isLoading}
                emptyMessage="No cameras found. Click 'Add Camera' to create one."
                hoverable
              />
              
              {/* Pagination Controls */}
              {totalElements > 0 && (
                <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-border-default">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs text-text-muted">Show</span>
                    <Select
                      value={itemsPerPage.toString()}
                      onChange={(e) => handleItemsPerPageChange(e.target.value)}
                      options={[
                        { label: '10', value: '10' },
                        { label: '25', value: '25' },
                        { label: '50', value: '50' },
                        { label: '100', value: '100' },
                      ]}
                      className="w-20"
                    />
                    <span className="text-xs text-text-muted">
                      Showing {startIndex + 1} to {endIndex} of {totalElements} cameras
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      leftIcon={<ChevronLeft size={16} />}
                    >
                      Previous
                    </Button>
                    
                    <span className="sm:hidden text-xs text-text-muted">{currentPage} / {totalPages}</span>
                    <div className="hidden sm:flex items-center gap-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter(page => {
                          return (
                            page === 1 ||
                            page === totalPages ||
                            (page >= currentPage - 1 && page <= currentPage + 1)
                          );
                        })
                        .map((page, index, array) => {
                          const showEllipsisBefore = index > 0 && page - array[index - 1] > 1;
                          return (
                            <React.Fragment key={page}>
                              {showEllipsisBefore && (
                                <span className="px-2 text-text-muted">...</span>
                              )}
                              <Button
                                variant={currentPage === page ? 'primary' : 'ghost'}
                                size="sm"
                                onClick={() => handlePageChange(page)}
                                className="min-w-[2.5rem]"
                              >
                                {page}
                              </Button>
                            </React.Fragment>
                          );
                        })}
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages || totalPages === 0}
                      rightIcon={<ChevronRight size={16} />}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </Card>
      </div>

      {/* Create Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          resetForm();
        }}
        title="Add New Camera"
        size="lg"
        footer={
          <div className="flex justify-end gap-3">
            <Button
              variant="ghost"
              onClick={() => {
                setIsCreateModalOpen(false);
                resetForm();
              }}
            >
              Cancel
            </Button>
            <Button variant="primary" onClick={handleCreate}>
              Create Camera
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Camera Name"
              placeholder="e.g., Front Door"
              value={formData.camera_location}
              onChange={(e) => setFormData({ ...formData, camera_location: e.target.value })}
              error={formErrors.camera_location}
              fullWidth
            />
            <Select
              label="Location"
              options={locations.map(loc => ({ 
                label: loc.ycl_name, 
                value: loc.ycl_id.toString() 
              }))}
              value={formData.location_id}
              onChange={(e) => setFormData({ ...formData, location_id: e.target.value })}
              error={formErrors.location_id}
              fullWidth
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Company"
              options={companyOptions}
              value={formData.camera_company}
              onChange={(e) => setFormData({ ...formData, camera_company: e.target.value })}
              error={formErrors.camera_company}
              fullWidth
            />
            <Input
              label="Model"
              placeholder="e.g., DS-2CD2147G2-L"
              value={formData.camera_model}
              onChange={(e) => setFormData({ ...formData, camera_model: e.target.value })}
              error={formErrors.camera_model}
              fullWidth
            />
          </div>
          <Input
            label="MAC Address"
            placeholder="e.g., 00:1A:2B:3C:4D:5E"
            value={formData.camera_mac}
            onChange={(e) => setFormData({ ...formData, camera_mac: e.target.value })}
            error={formErrors.camera_mac}
            fullWidth
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <Input
                label="IP Address"
                placeholder="e.g., 192.168.1.100"
                value={formData.camera_ip}
                onChange={(e) => setFormData({ ...formData, camera_ip: e.target.value })}
                error={formErrors.camera_ip}
                fullWidth
              />
            </div>
            <Input
              label="Port"
              type="number"
              placeholder="e.g., 8080"
              value={formData.camera_port}
              onChange={(e) => setFormData({ ...formData, camera_port: e.target.value })}
              error={formErrors.camera_port}
              fullWidth
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Username"
              placeholder="Camera username"
              value={formData.camera_username}
              onChange={(e) => setFormData({ ...formData, camera_username: e.target.value })}
              error={formErrors.camera_username}
              fullWidth
            />
            <Input
              label="Password"
              type="password"
              placeholder="Camera password"
              value={formData.camera_password}
              onChange={(e) => setFormData({ ...formData, camera_password: e.target.value })}
              error={formErrors.camera_password}
              fullWidth
            />
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedCamera(null);
        }}
        title="Delete Camera"
        size="sm"
        footer={
          <div className="flex justify-end gap-3">
            <Button
              variant="ghost"
              onClick={() => {
                setIsDeleteModalOpen(false);
                setSelectedCamera(null);
              }}
            >
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete}>
              Delete
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <Alert
            variant="warning"
            title="Warning"
            message="This action cannot be undone. All recordings and events associated with this camera will remain but won't be linked to this camera anymore."
          />
          <p className="text-text-secondary">
            Are you sure you want to delete <strong className="text-text-primary">{selectedCamera?.camera_location}</strong>?
          </p>
        </div>
      </Modal>

      {/* Edge Sync Modal */}
      <EdgeSyncModal
        isOpen={isEdgeSyncModalOpen}
        onClose={() => setIsEdgeSyncModalOpen(false)}
        locationId={selectedLocation !== 'all' ? parseInt(selectedLocation) : 0}
        locationName={locations.find(l => l.ycl_id.toString() === selectedLocation)?.ycl_name || ''}
        onSyncComplete={fetchCameras}
      />

      {/* Edge Provision Modal */}
      <EdgeProvisionModal
        isOpen={isEdgeProvisionModalOpen}
        onClose={() => setIsEdgeProvisionModalOpen(false)}
        locationId={selectedLocation !== 'all' ? parseInt(selectedLocation) : 0}
        locationName={locations.find(l => l.ycl_id.toString() === selectedLocation)?.ycl_name || ''}
        location={locations.find(l => l.ycl_id.toString() === selectedLocation)}
        onProvisionComplete={fetchCameras}
      />
    </MainLayout>
  );
};

export default CamerasPage;
